import { eventPost } from '@/lib/events';
import { neutralizeLabRow } from '@/logic/labResults';
import { labelCheckinRow } from '@/logic/checkin';
import { isSpruceRow } from '@/logic/chatMessage';

export type AiInsightMode = 'dashboard' | 'patient';

export interface AiInsightInput {
  mode: AiInsightMode;
  role?: string;
  withingsUserId?: string | number | null;
}

export interface ConversationMessage {
  source?: string;
  direction?: 'inbound' | 'outbound';
  body?: string;
  sent_at?: string;
  channel?: string;
  msg_type?: string | null;
  message_type?: string | null;
}

export async function getAIInsight(
  { mode, role = 'clinician', withingsUserId = null }: AiInsightInput,
): Promise<unknown> {
  if (!mode || !['dashboard', 'patient'].includes(mode)) {
    throw new Error('getAIInsight: mode must be "dashboard" or "patient"');
  }
  if (mode === 'patient' && !withingsUserId) {
    throw new Error('getAIInsight: patient mode requires withingsUserId');
  }
  const body: Record<string, unknown> = { mode, role };
  if (withingsUserId) body.id_dev_user = String(withingsUserId);
  return eventPost('ai_insight_requested', body);
}

// Selects today's messages if any, otherwise last 10 — keeps the suggestion context recent.
export async function getSuggestedReplies(
  withingsUserId: string | number,
  conversationMessages: ConversationMessage[] = [],
): Promise<unknown> {
  if (!withingsUserId) throw new Error('getSuggestedReplies: withingsUserId required');
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  // Of the clinic line, only a text is words to answer: a call or a transcript is dropped before the window,
  // so it cannot crowd a message out (a transcript carries anyone's words, and no model reads them).
  const spoken = conversationMessages.filter((m) => !isSpruceRow(m) || m.msg_type === 'spruce_sms');
  const today = spoken.filter((m) => m.sent_at && new Date(m.sent_at) >= todayStart);
  const messages = today.length > 0 ? today : spoken.slice(-10);
  return eventPost('ai_chat_suggestions', {
    id_dev_user: String(withingsUserId),
    // Lab values and a check-in reply's words (911, a symptom, the first name) never reach the model or WF5's
    // saved executions: each row becomes its placeholder.
    conversation: messages.map((m) => labelCheckinRow(neutralizeLabRow({
      source: m.source,
      direction: m.direction,
      body: m.body,
      sent_at: m.sent_at,
      channel: m.channel,
      // WF34 closes a question only on a staff reply or an Ava `ai_answer`, never an acknowledgement.
      msg_type: m.msg_type ?? m.message_type ?? null,
    }))),
  });
}
